import { useState } from 'react';
import { login, requestEmailLink } from './api.js';

// The admin sign-in gate. Screens never see this page - they pair instead,
// because a living-room display has no keyboard.
//
// One address field, and no choice to make before typing it. There is no
// "create an account" mode because there is nothing different to do: the same
// field and the same button serve somebody coming back and somebody arriving
// for the first time, and the server answers both identically so that this
// form cannot be used to ask whether an address is registered. Which of the
// two it was is decided in the mailbox.
//
// The password field is still here, behind a link, for accounts that have
// chosen one. It is second because it is now the unusual case.
export default function LoginPage({ onSignedIn }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [usePassword, setUsePassword] = useState(false);
  // Where the link was sent, which is also what switches this page into the
  // "go and look in your inbox" state. Held rather than derived, because the
  // address in the field can be edited afterwards and the message has already
  // gone to whatever it said at the time.
  const [sentTo, setSentTo] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    setBusy(true);
    try {
      if (usePassword) {
        await login(email, password);
        // The result is not used: the shell re-asks the server who we are,
        // because only it is entitled to say what this account may do.
        onSignedIn();
        return;
      }
      const result = await requestEmailLink(email);
      setSentTo(result.email || email);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  if (sentTo) {
    return (
      <main className="page-shell login-page">
        <div className="panel login-panel">
          <p className="eyebrow">Litbyt</p>
          <h1>Check your email</h1>
          <p className="panel-hint">
            If that address can be signed in with, a link is on its way to{' '}
            <strong>{sentTo}</strong>. Open it in this browser or any other — it works once,
            and expires in twenty minutes.
          </p>
          <p className="panel-hint">
            Nothing arriving? Check the spam folder, then try again — a mistyped address
            looks exactly like this too.
          </p>
          <button
            type="button"
            className="save-button"
            onClick={() => {
              setSentTo('');
              setError('');
            }}
          >
            Use a different address
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="page-shell login-page">
      <form className="panel login-panel" onSubmit={handleSubmit}>
        <p className="eyebrow">Litbyt</p>
        <h1>Sign in</h1>
        <p className="panel-hint">
          {usePassword
            ? 'Signing in with a password, for accounts that have set one.'
            : 'Enter your email and we will send you a link. No account yet? The link makes one.'}
        </p>

        <label className="field">
          <span>Email</span>
          <input
            type="email"
            autoComplete="username"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
        </label>

        {usePassword && (
          <label className="field">
            <span>Password</span>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </label>
        )}

        <button type="submit" className="save-button" disabled={busy}>
          {busy ? 'Working…' : usePassword ? 'Sign in' : 'Email me a link'}
        </button>

        {error && <p className="login-error">{error}</p>}

        <p className="login-switch">
          <button
            type="button"
            onClick={() => {
              setUsePassword((current) => !current);
              setError('');
              setPassword('');
            }}
          >
            {usePassword ? 'Email me a link instead' : 'Use a password instead'}
          </button>
        </p>
      </form>
    </main>
  );
}
